import { useMemo } from "react";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { BASE_PACK } from "../../../../shared/products/performance/config";
import {
  useGetCoachingRecordsQuery,
  useGetMyPerformancePermissionsQuery,
  useGetMyPerformanceScopeQuery,
  useGetPerformanceConfigQuery,
  useGetPlansQuery,
  useGetTodosQuery,
} from "../api/performanceApi";
import { PERFORMANCE_ROLE_NAMES } from "../../../../shared/products/performance/roleNames";
import { PERF_NAV_IDS, SHIPPED_FEATURES, type PerformanceNavContext } from "../nav";
import { useLabelDraft } from "./labelDraft";

/**
 * The nav context, loaded for the instance; `ready` once both answers (or their skips) are in.
 *
 * A UI hint only: it decides which performance links to draw. Every performance route on the
 * server gates on the product and its own permission whatever this says.
 *
 * Memoised: the route guard's effect keys on the sections built from this, and a fresh object
 * every render would re-run that effect on every render.
 */
export function usePerformanceNavContext(
  instanceId: string | null,
  enabled: boolean,
  hireCanManageUsers: boolean,
): PerformanceNavContext & { ready: boolean; managesPerformanceUsers: boolean } {
  const arg = enabled && instanceId ? instanceId : skipToken;
  const perms = useGetMyPerformancePermissionsQuery(arg);
  const config = useGetPerformanceConfigQuery(arg);
  const permsData = perms.data;
  const resolved = config.data?.config;
  const ready = !enabled || (!perms.isLoading && !config.isLoading);
  // E5 (core.js:155), ruling 2026-10-12: a manager sees Visits when they may run them (a visit is
  // the reviewer's and HR's only); HR always. Since ruling 2026-10-14 that is the run level or HR's
  // "Can run site visits" grant, so it is the server's own answer (scope.canRunVisits), never
  // re-derived here. The scope is read only for a manager whose Visits switch is on.
  const visitsOn =
    enabled &&
    !!instanceId &&
    permsData?.navigation?.showPerfVisits === true &&
    permsData?.role === PERFORMANCE_ROLE_NAMES.manager;
  const scope = useGetMyPerformanceScopeQuery(visitsOn && instanceId ? instanceId : skipToken);
  const seesVisits = !visitsOn || !resolved || !scope.data || scope.data.canRunVisits === true;
  // E9 O-5: the sidebar follows HR's unsaved Roles and wording draft as they type.
  const draft = useLabelDraft(enabled ? instanceId : null);
  const terms = draft?.terms ?? resolved?.terms;
  // E6 (§3.6.6): the caller's own open to-dos for the item's count. Counts only, and only once the
  // role's switch is known to be on; the layout and the sidebar both call this hook, and RTK
  // answers both from one request.
  const todosOn =
    enabled &&
    !!instanceId &&
    SHIPPED_FEATURES.includes(PERF_NAV_IDS.PERF_TODOS) &&
    permsData?.navigation?.showPerfTodos === true;
  const todos = useGetTodosQuery(
    todosOn && instanceId ? { instanceId, tab: "mine", pageSize: 0 } : skipToken,
  );
  const openTodos = todosOn ? todos.currentData?.counts.mine : undefined;
  // E3: the plans waiting on the caller's decision, counts only, once Plans is known to be drawn.
  const plansOn =
    enabled &&
    !!instanceId &&
    SHIPPED_FEATURES.includes(PERF_NAV_IDS.PERF_PLANS) &&
    permsData?.navigation?.showPerfPlans === true;
  const plans = useGetPlansQuery(plansOn && instanceId ? { instanceId, pageSize: 0 } : skipToken);
  const plansWaiting = plansOn ? plans.currentData?.counts.waitingOnMe : undefined;
  // WF-NAV-1: the records waiting on an employee, for "My <coaching>"'s count (core.js:272).
  // Counts only, from the log's own endpoint, so it agrees with Home and the page (A-15).
  const coachingOn =
    enabled &&
    !!instanceId &&
    SHIPPED_FEATURES.includes(PERF_NAV_IDS.PERF_COACHING) &&
    permsData?.role === PERFORMANCE_ROLE_NAMES.employee &&
    permsData?.navigation?.showPerfCoaching === true;
  const coaching = useGetCoachingRecordsQuery(
    coachingOn && instanceId ? { instanceId, pageSize: 0 } : skipToken,
  );
  const coachingWaiting = coachingOn
    ? coaching.currentData?.counts.waitingOnAcknowledgement
    : undefined;
  // Whose list it is (E-15), from the role's permissions already loaded: HR alone manages users,
  // an employee files nothing.
  const permissions = permsData?.permissions;
  const todosRole: PerformanceNavContext["todosRole"] = !permissions
    ? undefined
    : permissions["performance.users.manage"] === true
      ? "hr"
      : permissions["performance.coaching.create"] === true
        ? "manager"
        : "employee";
  return useMemo(
    () => ({
      navigation: permsData?.navigation ?? {},
      // Not `??`: a vertical without site visits answers `null`, which must not fall back to the
      // base pack's level. The base pack only stands in while the config has not arrived.
      visitsApply: (resolved ? resolved.visits.runMinLevel : BASE_PACK.visits.runMinLevel) !== null,
      seesVisits,
      canManageUsers:
        hireCanManageUsers || permsData?.permissions?.["performance.users.manage"] === true,
      features: SHIPPED_FEATURES,
      terms,
      // performance.users.manage alone (perf_hr): the guard's Users-page carve-out (spec §5) and
      // the nav's Positions / Roles and wording items (Task 16). Not `canManageUsers`, which also
      // carries the hire grant the hire rule already decides.
      managesPerformanceUsers:
        enabled && permsData?.permissions?.["performance.users.manage"] === true,
      todosRole,
      openTodos,
      plansWaiting,
      coachingWaiting,
      // E7 O-4: the employee's sidebar is the wireframe's (no Settings, "My <coaching>").
      performanceRole: permsData?.role ?? null,
      ready,
    }),
    [
      permsData,
      resolved,
      seesVisits,
      terms,
      hireCanManageUsers,
      ready,
      enabled,
      todosRole,
      openTodos,
      plansWaiting,
      coachingWaiting,
    ],
  );
}
